// The bubble of a variable price's rules: one element at body level, opened next to the "**" or the
// "see rules" note of a variable product, on a product's tile (eshop/ui/list.js), in the floating cart
// (FloatingCart.vue), under the Cart column's total (CartTotal.vue) and on a cart or quote line
// (SubscriptionCard.vue), the last three through PriceRuleTrigger.vue. The same bubble shows a promotion's
// text, opened from its band on a product's tile (eshop/ui/list.js).
//
// The rules are kept here, never in the page: the e-shop's own scripts register them under a key
// (register_rules), from the catalogue's rows and the cart's and quote's payloads, and a .price-rule-trigger
// button, or a promotion's .promotion-trigger, carries that key alone, in data-price-rule-key. A rule is
// {name, html, lang}: name optional (the floating cart and the Cart column list their products), html the
// rule the server cleaned (eshop/price_rule.py, sanitize_html last) or the promotion's text it built from
// escaped parts (eshop/promotion.py, get_bubble_html), lang the language of an English rule standing in for
// the visitor's. That html is the only HTML this bubble puts in the page, and it never comes from the page: a
// trigger whose key nothing registered opens nothing, so markup saved elsewhere on a website page (rich text
// keeps class and data-* attributes) cannot make the bubble insert any of its own. A product name is set
// as text.
//
// It behaves as the related products' popup does (www/products.js, left as it is): role="dialog" and
// aria-expanded on its trigger, focus moved into it, closed by Escape (focus back on the trigger), a click
// outside, a scroll of the page or a resize, placed under its trigger or above it, inside the viewport.
// One difference: a scroll inside the bubble keeps it open, so a long rule can be read to the end.
//
// Delegated on the document, from the bundle every e-shop page loads (hooks.py web_include_js): a tile is
// rebuilt on every change, and www/products.js, inlined as a classic script, cannot import a module.

frappe.provide("ioi_partners")

const POPUP_ID = "ioi-price-rule-popup"
const TRIGGER = ".price-rule-trigger, .promotion-trigger"
const GAP = 8     // between the trigger and the bubble
const MARGIN = 8  // from any viewport edge

// key -> [{name, html, lang}], as register_rules() was given them.
const rules_by_key = new Map()
// key -> the bubble's name for that key's rules, as register_rules() was given it: a promotion's title. Without
// one, the bubble is named "Variable price".
const label_by_key = new Map()
let last_key = 0

let popup = null
let current_trigger = null
let current_key = null

// A key no trigger uses yet, for a trigger of its own (PriceRuleTrigger.vue).
export function new_rules_key() {
	last_key += 1
	return `price-rule-${last_key}`
}

// Keep rules under key, for the triggers that carry it: a tile's under its product, and its promotion's under a
// key of its own (eshop/ui/list.js), a PriceRuleTrigger's under its own (new_rules_key). label names the bubble
// for that key: a promotion's title; none for a variable price's rules. A bubble showing that key's rules closes:
// what it shows may be out of date, and its trigger may be gone with a rebuilt tile or a trigger turned to plain
// text.
export function register_rules(key, rules, label = "") {
	rules_by_key.set(key, Array.isArray(rules) ? rules : [])
	label_by_key.set(key, label)
	if (current_key === key) {
		close(false)
	}
}

// Forget key's rules, and close the bubble when it shows them: their trigger is going away.
export function unregister_rules(key) {
	rules_by_key.delete(key)
	label_by_key.delete(key)
	if (current_key === key) {
		close(false)
	}
}

function get_popup() {
	if (popup) {
		return popup
	}

	popup = document.createElement("div")
	popup.id = POPUP_ID
	popup.setAttribute("role", "dialog")
	popup.setAttribute("aria-label", __("Variable price"))
	popup.setAttribute("tabindex", "-1")
	document.body.appendChild(popup)
	return popup
}

function is_open() {
	return !!popup && popup.classList.contains("is-open")
}

// The key a trigger carries: the one thing the bubble reads from a trigger, besides its own aria state.
function key_of(element) {
	return element.dataset.priceRuleKey
}

// The rules registered under the trigger's key that have a text; none for a key nothing registered.
function read_rules(trigger) {
	const rules = rules_by_key.get(key_of(trigger)) || []
	return rules.filter((rule) => rule && rule.html)
}

// Where the focus goes back: the trigger itself, or, for a tile's price "**", which is hidden from
// assistive technology (eshop/ui/list.js), the tile's note, which carries the same key.
function focus_target(trigger, key) {
	if (trigger.getAttribute("aria-hidden") !== "true") {
		return trigger
	}
	const tile = trigger.closest(".product-tile") || document
	for (const candidate of tile.querySelectorAll(TRIGGER)) {
		if (key_of(candidate) === key && candidate.getAttribute("aria-hidden") !== "true") {
			return candidate
		}
	}
	return trigger
}

// Replaced wholesale on every open: nothing of the previous trigger's rules survives.
function fill(rules) {
	const element = get_popup()
	element.replaceChildren()
	for (const rule of rules) {
		const entry = document.createElement("div")
		entry.className = "ioi-price-rule"
		if (rule.name) {
			const name = document.createElement("div")
			name.className = "ioi-price-rule__name"
			name.textContent = rule.name
			entry.appendChild(name)
		}
		// .ql-snow, as the family description's block (www/products.html): Quill's stylesheet draws the
		// Desk editor's markup (.ql-editor) as the Desk does.
		const text = document.createElement("div")
		text.className = "ioi-price-rule__text ql-snow"
		if (rule.lang) {
			text.setAttribute("lang", rule.lang)
		}
		// Registered by the e-shop's own scripts, cleaned by the server: never read from the page.
		text.innerHTML = rule.html
		entry.appendChild(text)
		element.appendChild(entry)
	}
}

export function open(trigger) {
	const rules = read_rules(trigger)
	if (!rules.length) {
		return
	}

	// From another trigger: its state is cleared without moving the focus back to it.
	close(false)
	const element = get_popup()
	current_trigger = trigger
	current_key = key_of(trigger)
	// Named for what it shows: the promotion's title, or "Variable price".
	element.setAttribute("aria-label", label_by_key.get(current_key) || __("Variable price"))
	trigger.setAttribute("aria-expanded", "true")
	element.classList.add("is-open")
	// Filled before it is placed: the placement measures its height.
	fill(rules)
	position(trigger)
	element.focus()
}

export function close(refocus = true) {
	if (!is_open()) {
		return
	}

	// Read before the bubble is hidden, which takes the focus out of it.
	const focus_inside = popup.contains(document.activeElement)
	popup.classList.remove("is-open")
	const trigger = current_trigger
	const key = current_key
	current_trigger = null
	current_key = null
	if (!trigger) {
		return
	}

	trigger.setAttribute("aria-expanded", "false")
	// Back on the trigger (focus_target) when it was dismissed on purpose (Escape, the trigger again), and
	// when the focus was in the bubble as a scroll or a resize closed it, where it would fall to the page's
	// body - without scrolling the page then. Not on a click elsewhere, where the focus belongs to what was
	// clicked.
	const target = focus_target(trigger, key)
	if ((refocus || focus_inside) && target.isConnected) {
		target.focus({ preventScroll: !refocus })
	}
}

// As the related products' popup is placed: right edges aligned, moved left when it would cross the
// viewport, under the trigger or above it when there is no room. position:fixed, so the viewport
// coordinates of getBoundingClientRect() apply as they are.
function position(trigger) {
	const anchor = trigger.getBoundingClientRect()
	const box = popup.getBoundingClientRect()

	let left = anchor.right - box.width
	left = Math.min(left, window.innerWidth - MARGIN - box.width)
	left = Math.max(left, MARGIN)

	let top = anchor.bottom + GAP
	if (top + box.height > window.innerHeight - MARGIN) {
		const above = anchor.top - GAP - box.height
		top = above >= MARGIN ? above : Math.max(MARGIN, window.innerHeight - MARGIN - box.height)
	}

	popup.style.left = `${Math.round(left)}px`
	popup.style.top = `${Math.round(top)}px`
}

function install() {
	document.addEventListener("click", (e) => {
		const trigger = e.target.closest(TRIGGER)
		if (trigger) {
			if (trigger.getAttribute("aria-expanded") === "true") {
				close()
			} else {
				open(trigger)
			}
			return
		}
		if (is_open() && !e.target.closest(`#${POPUP_ID}`)) {
			close(false)
		}
	})

	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && is_open()) {
			close()
		}
	})

	// Closed rather than moved: position:fixed, it would leave its trigger behind. Capture phase, so a
	// scroll of any scrolling ancestor counts too - except the bubble's own.
	window.addEventListener("scroll", (e) => {
		if (!is_open()) {
			return
		}
		if (e.target instanceof Node && popup.contains(e.target)) {
			return
		}
		close(false)
	}, true)

	window.addEventListener("resize", () => {
		if (is_open()) {
			close(false)
		}
	})
}

// Once per page, whatever loads the bundle. eshop/ui/list.js, a classic script's class, registers its
// tiles' rules through this handle; the Vue components import the functions.
if (!ioi_partners.price_rule_bubble) {
	ioi_partners.price_rule_bubble = { register_rules, unregister_rules, new_rules_key, open, close }
	install()
}
